import { useContext, useEffect, useState } from "react";
import { ProgramMember, ProgramMemberOptions, ProgramMemberOption } from "../../../../interfaces/program";
import { createProgramMember, deleteProgramMember, deleteProgramMemberBulk, getProgramMembers, getProgramMemberOptions, getProgramClubMembers, getProgramTeamMembers } from "../../../../services/program";
import { Button, Form, Modal } from "react-bootstrap";
import { t } from "i18next";
import { LoadingSpinner } from "../../../general/loading-spinner";
import DataTable from "react-data-table-component";
import moment from "moment";
import { getPaginationOptions, tableStyles } from "../../../../util/data-table";
import { getClubOptions } from "../../../../services/club";
import { getTeamOptions } from "../../../../services/team";
import { Club } from "../../../../interfaces/club";
import { UserContext } from "../../../../App";

interface ProgramMembersProps {
    program_id: number;
    created_by: number;
    onClose: CallableFunction;
}

export function ProgramMembers(props: ProgramMembersProps) {
    const { userProfile } = useContext(UserContext);
    const is_owner = userProfile.id == props.created_by; // Usar == en lugar de === para comparar sin tipo estricto
    const can_manage_members = is_owner; // Solo el propietario puede gestionar miembros

    const [loaded, setLoaded] = useState(false);
    const [members, setMembers] = useState<ProgramMember[]>([]);
    const [showAdd, setShowAdd] = useState(false);
    const [memberOptions, setMemberOptions] = useState<ProgramMemberOptions>({ clubs: [], teams: [], users: [] });
    const [selectedMembers, setSelectedMembers] = useState<any[]>([]);
    const [clubOptions, setClubOptions] = useState<Club[]>([]);
    const [teamOptions, setTeamOptions] = useState<any[]>([]);
    const [selectedClub, setSelectedClub] = useState(0);
    const [selectedTeam, setSelectedTeam] = useState(0);
    const [selectedEntityType, setSelectedEntityType] = useState('');
    const [searchText, setSearchText] = useState('');
    const [filteredOptions, setFilteredOptions] = useState<{ clubs: ProgramMemberOption[], teams: ProgramMemberOption[], users: ProgramMemberOption[] }>({ clubs: [], teams: [], users: [] });
    const [selectAll, setSelectAll] = useState(false);
    const [loadingOptions, setLoadingOptions] = useState(false);
    const [selectedMembersToDelete, setSelectedMembersToDelete] = useState<number[]>([]);
    const [expandedMembersData, setExpandedMembersData] = useState<{ [key: number]: any[] }>({});
    const [loadingExpandedRows, setLoadingExpandedRows] = useState<{ [key: number]: boolean }>({});

    useEffect(() => {
        Promise.allSettled([
            getProgramMembers(props.program_id),
            getClubOptions()
        ]).then(responses => {
            if (responses[0].status === 'fulfilled') {
                setMembers(responses[0].value.data);
            }
            if (responses[1].status === 'fulfilled') {
                setClubOptions(responses[1].value.data);
            }
            setLoaded(true);
        }).catch((error: any) => {
            console.log(error);
        });
    }, []);

    const handleClose = () => {
        props.onClose();
    }

    const handleSave = (event: any) => {
        event.preventDefault();

        if (selectedMembers.length === 0) {
            alert(t('Please select at least one member'));
            return;
        }

        const body = {
            program_id: props.program_id,
            members: selectedMembers.map(m => ({
                entity_type: m.entity_type,
                entity_id: m.entity_id
            }))
        }

        createProgramMember(body)
            .then((response: any) => {
                if (response.data.status == 1) {
                    // Recargar la lista de miembros
                    getProgramMembers(props.program_id)
                        .then((res: any) => {
                            setMembers(res.data);
                            setShowAdd(false);
                            setSelectedMembers([]);
                            setSelectedClub(0);
                            setSelectedTeam(0);
                            setSelectedEntityType('');
                        })
                        .catch((error: any) => {
                            console.log(error);
                        });
                } else {
                    alert(response.data.message);
                }
            })
            .catch((error: any) => {
                console.log(error);
                alert(t('Error adding members'));
            });
    }

    const handleDelete = (event: any) => {
        event.preventDefault();

        if (!window.confirm(t('Are you sure you want to delete this member?'))) {
            return;
        }

        const id = event.currentTarget.dataset.id;
        deleteProgramMember(event.currentTarget.dataset.id)
            .then((response: any) => {
                if (response.data == 1) {
                    const newMembers = members.filter((m: ProgramMember) => m.id != id);
                    setMembers(newMembers);
                }
            })
            .catch((error: any) => {
                console.log(error);
            });
    }

    const handleDeleteSelected = () => {
        if (selectedMembersToDelete.length === 0) {
            alert(t('Please select at least one member to delete'));
            return;
        }

        const count = selectedMembersToDelete.length;
        if (!window.confirm(t('Are you sure you want to delete {count} members?').replace('{count}', count.toString()))) {
            return;
        }

        // Borrar todos los miembros seleccionados usando el endpoint bulk
        deleteProgramMemberBulk(selectedMembersToDelete)
            .then((response: any) => {
                const { status, deleted_count, failed_ids, message } = response.data;

                if (status == "1") {
                    // Filtrar los miembros que se borraron exitosamente
                    const newMembers = members.filter((m: ProgramMember) => !selectedMembersToDelete.includes(m.id) || failed_ids.includes(m.id));
                    setMembers(newMembers);
                    setSelectedMembersToDelete([]);

                    if (failed_ids.length > 0) {
                        alert(message); // Muestra el mensaje con los IDs que fallaron
                    }
                } else {
                    alert(message || t('Error deleting members'));
                }
            })
            .catch((error: any) => {
                console.log(error);
                alert(t('Error deleting members'));
            });
    }

    const handleToggleMemberSelection = (memberId: number) => {
        if (selectedMembersToDelete.includes(memberId)) {
            setSelectedMembersToDelete(selectedMembersToDelete.filter(id => id !== memberId));
        } else {
            setSelectedMembersToDelete([...selectedMembersToDelete, memberId]);
        }
    }

    const handleToggleAllMembers = () => {
        if (selectedMembersToDelete.length === members.length) {
            setSelectedMembersToDelete([]);
        } else {
            setSelectedMembersToDelete(members.map(m => m.id));
        }
    }

    const handleAddNew = (event: any) => {
        event.preventDefault();
        loadMemberOptions();
        setShowAdd(true);
    }

    const loadMemberOptions = () => {
        setLoadingOptions(true);
        getProgramMemberOptions(props.program_id, selectedClub, selectedTeam, selectedEntityType)
            .then((response: any) => {
                setMemberOptions(response.data);
                applyTextFilter(response.data);
            })
            .catch((error: any) => {
                console.log(error);
            })
            .finally(() => {
                setLoadingOptions(false);
            });
    }

    const applyTextFilter = (options: ProgramMemberOptions) => {
        if (searchText.trim() === '') {
            setFilteredOptions(options);
            setSelectAll(false);
            return;
        }

        const search = searchText.toLowerCase();
        const filtered = {
            clubs: options.clubs.filter((c: ProgramMemberOption) =>
                c.name.toLowerCase().includes(search)
            ),
            teams: options.teams.filter((t: ProgramMemberOption) =>
                t.name.toLowerCase().includes(search) ||
                (t.club_name && t.club_name.toLowerCase().includes(search))
            ),
            users: options.users.filter((u: ProgramMemberOption) =>
                u.name.toLowerCase().includes(search) ||
                (u.email && u.email.toLowerCase().includes(search))
            )
        };
        setFilteredOptions(filtered);
        setSelectAll(false);
    }

    const handleClubChange = (event: any) => {
        const clubId = parseInt(event.target.value || '0');
        setSelectedClub(clubId);
        setSelectedTeam(0);
        setTeamOptions([]);

        if (clubId > 0) {
            getTeamOptions(clubId.toString())
                .then((response: any) => {
                    setTeamOptions(response.data);
                })
                .catch((error: any) => {
                    console.log(error);
                });
        }

        // Recargar opciones con el nuevo filtro de club
        setTimeout(() => {
            setLoadingOptions(true);
            getProgramMemberOptions(props.program_id, clubId, 0, selectedEntityType)
                .then((response: any) => {
                    setMemberOptions(response.data);
                    applyTextFilter(response.data);
                })
                .catch((error: any) => {
                    console.log(error);
                })
                .finally(() => {
                    setLoadingOptions(false);
                });
        }, 0);
    }

    const handleTeamChange = (event: any) => {
        const teamId = parseInt(event.target.value || '0');
        setSelectedTeam(teamId);

        // Recargar opciones con el nuevo filtro de equipo
        setTimeout(() => {
            setLoadingOptions(true);
            getProgramMemberOptions(props.program_id, selectedClub, teamId, selectedEntityType)
                .then((response: any) => {
                    setMemberOptions(response.data);
                    applyTextFilter(response.data);
                })
                .catch((error: any) => {
                    console.log(error);
                })
                .finally(() => {
                    setLoadingOptions(false);
                });
        }, 0);
    }

    const handleEntityTypeChange = (event: any) => {
        const entityType = event.target.value;
        setSelectedEntityType(entityType);

        // Recargar opciones con el nuevo filtro de tipo
        setLoadingOptions(true);
        getProgramMemberOptions(props.program_id, selectedClub, selectedTeam, entityType)
            .then((response: any) => {
                setMemberOptions(response.data);
                applyTextFilter(response.data);
            })
            .catch((error: any) => {
                console.log(error);
            })
            .finally(() => {
                setLoadingOptions(false);
            });
    }

    const handleSearchTextChange = (event: any) => {
        const text = event.target.value;
        setSearchText(text);
        applyTextFilter(memberOptions);
    }

    const handleSelectAll = (event: any) => {
        const checked = event.target.checked;
        setSelectAll(checked);

        if (checked) {
            const allMembers: any[] = [];
            filteredOptions.clubs.forEach((c: ProgramMemberOption) => {
                allMembers.push({ entity_type: 'club', entity_id: c.id, label: c.name });
            });
            filteredOptions.teams.forEach((t: ProgramMemberOption) => {
                allMembers.push({ entity_type: 'team', entity_id: t.id, label: t.name });
            });
            filteredOptions.users.forEach((u: ProgramMemberOption) => {
                allMembers.push({ entity_type: 'user', entity_id: u.id, label: u.name });
            });
            setSelectedMembers(allMembers);
        } else {
            setSelectedMembers([]);
        }
    }

    const handleSelectGroup = (groupType: 'clubs' | 'teams' | 'users', checked: boolean) => {
        if (checked) {
            const groupMembers: any[] = [];
            filteredOptions[groupType].forEach((item: ProgramMemberOption) => {
                const entityType = groupType === 'clubs' ? 'club' : groupType === 'teams' ? 'team' : 'user';
                groupMembers.push({ entity_type: entityType, entity_id: item.id, label: item.name });
            });
            setSelectedMembers([...selectedMembers, ...groupMembers]);
        } else {
            const entityType = groupType === 'clubs' ? 'club' : groupType === 'teams' ? 'team' : 'user';
            setSelectedMembers(selectedMembers.filter((m: any) => m.entity_type !== entityType));
        }
    }

    const isGroupSelected = (groupType: 'clubs' | 'teams' | 'users') => {
        const entityType = groupType === 'clubs' ? 'club' : groupType === 'teams' ? 'team' : 'user';
        return filteredOptions[groupType].length > 0 &&
               filteredOptions[groupType].every((item: ProgramMemberOption) =>
                   isMemberSelected(entityType, item.id)
               );
    }

    const handleMemberCheck = (entityType: string, entityId: number, label: string, checked: boolean) => {
        if (checked) {
            setSelectedMembers([...selectedMembers, { entity_type: entityType, entity_id: entityId, label }]);
        } else {
            setSelectedMembers(selectedMembers.filter((m: any) =>
                !(m.entity_type === entityType && m.entity_id === entityId)
            ));
            setSelectAll(false);
        }
    }

    const isMemberSelected = (entityType: string, entityId: number) => {
        return selectedMembers.some((m: any) => m.entity_type === entityType && m.entity_id === entityId);
    }

    const handleCloseAddNew = () => {
        setShowAdd(false);
        setSelectedMembers([]);
        setSelectedClub(0);
        setSelectedTeam(0);
        setSelectedEntityType('');
        setSearchText('');
        setSelectAll(false);
        setFilteredOptions({ clubs: [], teams: [], users: [] });
    }

    const handleToggleExpand = (row: ProgramMember) => {
        const rowId = row.id;

        // Si los datos ya están cargados, no hacer nada
        if (expandedMembersData[rowId]) {
            return;
        }

        // Cargar los miembros la primera vez que se expande
        setLoadingExpandedRows({ ...loadingExpandedRows, [rowId]: true });

        const fetchMembers = row.entity_type === 'club'
            ? getProgramClubMembers(props.program_id, row.entity_id)
            : getProgramTeamMembers(props.program_id, row.entity_id);

        fetchMembers
            .then((response: any) => {
                setExpandedMembersData({ ...expandedMembersData, [rowId]: response.data });
                setLoadingExpandedRows({ ...loadingExpandedRows, [rowId]: false });
            })
            .catch((error: any) => {
                console.log(error);
                setLoadingExpandedRows({ ...loadingExpandedRows, [rowId]: false });
            });
    }

    const CustomActions = (row: any) => (
        <span>
            <a href="#" className="icon-link delete" title={ t("Delete") } onClick={ handleDelete } data-id={ row.row.id }>
                <i className="bi bi-trash"></i>
            </a>
        </span>
    );

        const CustomDate = (row: any ) => (
            <span>
                { moment(row.row.measurement_date).format("DD/MM/YYYY") }
            </span>
        );
    
    const getEntityTypeLabel = (entity_type: string) => {
        switch (entity_type) {
            case 'club': return t('Club');
            case 'team': return t('Team');
            case 'user': return t('User');
            default: return entity_type;
        }
    }

    const getRoleLabel = (role: string | undefined) => {
        if (!role) return '';
        switch (role.toLowerCase()) {
            case 'athlete': return t('Athlete');
            case 'coach': return t('Coach');
            case 'manager': return t('Manager');
            case 'administrator': return t('Administrator');
            default: return role;
        }
    }

    const CustomEntityType = (row: any) => {
        let label = getEntityTypeLabel(row.row.entity_type);
        let bgColor = 'secondary';

        if (row.row.entity_type === 'club') {
            bgColor = 'primary';
        } else if (row.row.entity_type === 'team') {
            bgColor = 'success';
        } else if (row.row.entity_type === 'user' && row.row.role) {
            // Mostrar el rol específico del usuario en lugar de "Usuario"
            label = getRoleLabel(row.row.role);
            // Colores diferentes según el rol
            if (row.row.role.toLowerCase() === 'manager') {
                bgColor = 'info';
            } else if (row.row.role.toLowerCase() === 'coach') {
                bgColor = 'warning';
            } else if (row.row.role.toLowerCase() === 'athlete') {
                bgColor = 'secondary';
            }
        }

        return (
            <span className={`badge bg-${bgColor}`}>
                {label}
            </span>
        );
    };

    const CustomCheckbox = (row: any) => (
        <input
            type="checkbox"
            checked={selectedMembersToDelete.includes(row.row.id)}
            onChange={() => handleToggleMemberSelection(row.row.id)}
        />
    );

    const ExpandedComponent = ({ data }: { data: ProgramMember }) => {
        const members = expandedMembersData[data.id] || [];
        const isLoading = loadingExpandedRows[data.id];

        if (isLoading) {
            return (
                <div style={{ padding: '20px', textAlign: 'center' }}>
                    <LoadingSpinner />
                </div>
            );
        }

        if (members.length === 0) {
            return (
                <div style={{ padding: '20px', textAlign: 'center', color: '#6c757d' }}>
                    {t('No members found')}
                </div>
            );
        }

        return (
            <div style={{ padding: '15px 50px', backgroundColor: '#f8f9fa' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                        <tr style={{ borderBottom: '1px solid #dee2e6' }}>
                            <th style={{ padding: '6px 8px', textAlign: 'left', color: '#495057', fontWeight: 600, fontSize: '12px' }}>
                                {t('Name')}
                            </th>
                            <th style={{ padding: '6px 8px', textAlign: 'left', color: '#495057', fontWeight: 600, fontSize: '12px' }}>
                                {t('Email')}
                            </th>
                            <th style={{ padding: '6px 8px', textAlign: 'left', color: '#495057', fontWeight: 600, fontSize: '12px' }}>
                                {t('Role')}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {members.map((member: any, index: number) => (
                            <tr key={index} style={{ borderBottom: '1px solid #e9ecef' }}>
                                <td style={{ padding: '6px 8px', fontSize: '13px' }}>{member.name || member.display_name || '-'}</td>
                                <td style={{ padding: '6px 8px', fontSize: '13px' }}>{member.email || '-'}</td>
                                <td style={{ padding: '6px 8px', fontSize: '13px' }}>{getRoleLabel(member.role)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    const baseColumns = [
            {
                id: 'id',
                name: '#',
                style: { maxWidth: '80px' },
                selector: (row: any) => row.id,
                sortable: true
            },
            {
                id: 'entity_type',
                name: t('Type'),
                center: true,
                selector: (row: any) => row.entity_type,
                sortable: true,
                cell: (row: any) => <CustomEntityType row={row} />,
            },
            {
                id: 'entity_name',
                name: t("Name"),
                selector: (row: any) => row.entity_name,
                sortable: true,
                wrap: true
            },
            {
                id: 'entity_description',
                name: t('Description'),
                selector: (row: any) => row.entity_description,
                sortable: true,
                wrap: true
            },
            {
                id: 'created_at',
                name: t("Member since"),
                selector: (row: any) => row.created_at,
                sortable: true,
                cell: (row: any) => <CustomDate row={row} />,
            }
        ];

    // Solo añadir columnas de gestión si es el propietario
    const columns = can_manage_members ? [
        {
            id: 'select',
            name: (
                <input
                    type="checkbox"
                    checked={selectedMembersToDelete.length === members.length && members.length > 0}
                    onChange={handleToggleAllMembers}
                />
            ),
            cell: (row: any) => <CustomCheckbox row={row} />,
            width: '50px',
            allowOverflow: true
        },
        ...baseColumns,
        {
            id: 'actions',
            name: t("actions"),
            cell: (row: any) => <CustomActions row={row} />,
        }
    ] : baseColumns;

    return (
        <Modal show={ true }
            onHide={ handleClose } size="xl"> 
            <Modal.Header closeButton>
                <Modal.Title>
                    { t("Program members") }
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                { !loaded &&
                    <LoadingSpinner />
                }
                { loaded &&
                    <div className="program-members-container">
                        { can_manage_members && !showAdd &&
                            <div className="add-member-button-container mb-3">
                                <Button variant="primary" onClick={ handleAddNew }>
                                    <i className="bi bi-plus-circle me-2"></i>
                                    { t("Add members") }
                                </Button>
                            </div>
                        }
                        { can_manage_members && showAdd &&
                            <Form onSubmit={ handleSave } className="add-member-form">
                                <div className="form-header">
                                    <h4>{ t("Add members") }</h4>
                                    <button type="button" className="btn-close" onClick={ handleCloseAddNew } aria-label="Close"></button>
                                </div>
                                <div className="filters-section">
                                    <h6 className="section-title">{ t("Filters") }</h6>
                                    <div className="filter-controls">
                                        <Form.Group controlId="entity_type" className="field-member-type">
                                            <Form.Label>{ t("Member type") }</Form.Label>
                                            <Form.Select value={selectedEntityType} onChange={handleEntityTypeChange}>
                                                <option value="">{ t("All types") }</option>
                                                <option value="club">{ t("Club") }</option>
                                                <option value="team">{ t("Team") }</option>
                                                <option value="user_manager">{ t("Manager") }</option>
                                                <option value="user_coach">{ t("Coach") }</option>
                                                <option value="user_athlete">{ t("Athlete") }</option>
                                            </Form.Select>
                                        </Form.Group>
                                        <Form.Group controlId="club" className="field-club">
                                            <Form.Label>{ t("club") }</Form.Label>
                                            <Form.Select value={selectedClub} onChange={handleClubChange}>
                                                <option value="0">{ t("All clubs") }</option>
                                                { clubOptions.map((c: Club) => (
                                                    <option key={c.id} value={c.id}>{ c.name }</option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                        { selectedClub > 0 && teamOptions.length > 0 && (
                                            <Form.Group controlId="team" className="field-team">
                                                <Form.Label>{ t("Team") }</Form.Label>
                                                <Form.Select value={selectedTeam} onChange={handleTeamChange}>
                                                    <option value="0">{ t("All teams") }</option>
                                                    { teamOptions.map((t: any) => (
                                                        <option key={t.id} value={t.id}>{ t.name }</option>
                                                    ))}
                                                </Form.Select>
                                            </Form.Group>
                                        )}
                                        <Form.Group controlId="searchText" className="field-search">
                                            <Form.Label>{ t("Search by name or email") }</Form.Label>
                                            <Form.Control
                                                type="text"
                                                placeholder={ t("Write to search") }
                                                value={searchText}
                                                onChange={handleSearchTextChange}
                                            />
                                        </Form.Group>
                                    </div>
                                </div>
                                <div className="selection-section">
                                    <div className="section-header">
                                        <h6 className="section-title">{ t("Select members") }</h6>
                                        <Form.Check
                                            type="checkbox"
                                            id="select-all"
                                            label={t("Select all")}
                                            checked={selectAll}
                                            onChange={handleSelectAll}
                                            className="select-all-checkbox"
                                        />
                                    </div>
                                    <div className="member-selection-list">
                                        <Form.Control type="hidden" name="program_id" required defaultValue={ props.program_id } />

                                        {loadingOptions ? (
                                            <div className="text-center py-4">
                                                <LoadingSpinner />
                                            </div>
                                        ) : (filteredOptions.clubs.length > 0 || filteredOptions.teams.length > 0 || filteredOptions.users.length > 0) ? (
                                        <>
                                            {filteredOptions.clubs.length > 0 && (
                                                <div className="member-group">
                                                    <div className="group-header">
                                                        <Form.Check
                                                            type="checkbox"
                                                            id="select-all-clubs"
                                                            label={<strong>{t('Clubs')}</strong>}
                                                            checked={isGroupSelected('clubs')}
                                                            onChange={(e) => handleSelectGroup('clubs', e.target.checked)}
                                                            className="group-checkbox"
                                                        />
                                                    </div>
                                                    <div className="group-items">
                                                        {filteredOptions.clubs.map((c: ProgramMemberOption) => (
                                                            <Form.Check
                                                                key={`club-${c.id}`}
                                                                type="checkbox"
                                                                id={`club-${c.id}`}
                                                                label={
                                                                    <span>
                                                                        <span className="member-name">{c.name}</span>
                                                                        <span className="member-count text-muted ms-2">({c.members_count} {t('members')})</span>
                                                                    </span>
                                                                }
                                                                checked={isMemberSelected('club', c.id)}
                                                                onChange={(e) => handleMemberCheck('club', c.id, c.name, e.target.checked)}
                                                            />
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {filteredOptions.teams.length > 0 && (
                                                <div className="member-group">
                                                    <div className="group-header">
                                                        <Form.Check
                                                            type="checkbox"
                                                            id="select-all-teams"
                                                            label={<strong>{t('Teams')}</strong>}
                                                            checked={isGroupSelected('teams')}
                                                            onChange={(e) => handleSelectGroup('teams', e.target.checked)}
                                                            className="group-checkbox"
                                                        />
                                                    </div>
                                                    <div className="group-items">
                                                        {filteredOptions.teams.map((team: ProgramMemberOption) => (
                                                            <Form.Check
                                                                key={`team-${team.id}`}
                                                                type="checkbox"
                                                                id={`team-${team.id}`}
                                                                label={
                                                                    <span>
                                                                        <span className="member-name">{team.name}</span>
                                                                        <span className="text-muted ms-1">- {team.club_name}</span>
                                                                        <span className="member-count text-muted ms-2">({team.members_count} {t('members')})</span>
                                                                    </span>
                                                                }
                                                                checked={isMemberSelected('team', team.id)}
                                                                onChange={(e) => handleMemberCheck('team', team.id, team.name, e.target.checked)}
                                                            />
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {filteredOptions.users.length > 0 && (
                                                <div className="member-group">
                                                    <div className="group-header">
                                                        <Form.Check
                                                            type="checkbox"
                                                            id="select-all-users"
                                                            label={<strong>
                                                                {selectedEntityType === 'user_manager' ? t('Managers') :
                                                                 selectedEntityType === 'user_coach' ? t('Coaches') :
                                                                 selectedEntityType === 'user_athlete' ? t('Athletes') :
                                                                 t('Users')}
                                                            </strong>}
                                                            checked={isGroupSelected('users')}
                                                            onChange={(e) => handleSelectGroup('users', e.target.checked)}
                                                            className="group-checkbox"
                                                        />
                                                    </div>
                                                    <div className="group-items">
                                                        {filteredOptions.users.map((u: ProgramMemberOption) => (
                                                            <Form.Check
                                                                key={`user-${u.id}`}
                                                                type="checkbox"
                                                                id={`user-${u.id}`}
                                                                label={
                                                                    <span>
                                                                        <span className="member-name">{u.name}</span>
                                                                        <span className="text-muted ms-1">- {u.email}</span>
                                                                        <span className="member-role text-muted ms-2">({getRoleLabel(u.role)})</span>
                                                                    </span>
                                                                }
                                                                checked={isMemberSelected('user', u.id)}
                                                                onChange={(e) => handleMemberCheck('user', u.id, u.name, e.target.checked)}
                                                            />
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                            </>
                                        ) : (
                                            <div className="no-results">
                                                <p className="text-muted text-center">{t("No members found")}</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="form-footer">
                                    <Button variant="secondary" onClick={handleCloseAddNew}>
                                        { t("Cancel") }
                                    </Button>
                                    <Button type="submit" variant="primary" className="ms-2">
                                        <i className="bi bi-share me-2"></i>
                                        { t("Share") }
                                    </Button>
                                </div>
                            </Form>
                        }
                        <div className="subtable-container mt-4">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h5 className="mb-0">{ t("Current members") }</h5>
                                {can_manage_members && selectedMembersToDelete.length > 0 && (
                                    <Button variant="danger" size="sm" onClick={handleDeleteSelected}>
                                        <i className="bi bi-trash me-2"></i>
                                        { t("Delete selected") } ({selectedMembersToDelete.length})
                                    </Button>
                                )}
                            </div>
                            <DataTable
                                columns={ columns }
                                data={members}
                                responsive={ true }
                                striped={ true }
                                highlightOnHover={ true }
                                defaultSortFieldId={ 'entity_name' }
                                defaultSortAsc={ true }
                                pagination={ true }
                                customStyles={{
                                    ...tableStyles,
                                    headRow: {
                                        style: {
                                            backgroundColor: '#91231F',
                                        },
                                    },
                                    expanderButton: {
                                        style: {
                                            color: '#6c757d',
                                            '&:hover': {
                                                color: '#495057',
                                            },
                                        },
                                    },
                                }}
                                paginationComponentOptions={ getPaginationOptions() }
                                expandableRows
                                expandableRowsComponent={ExpandedComponent}
                                expandableRowDisabled={(row: ProgramMember) => row.entity_type === 'user'}
                                onRowExpandToggled={(toggleState: boolean, row: ProgramMember) => handleToggleExpand(row)}
                            />
                        </div>
                    </div>
                }
            </Modal.Body>
        </Modal>
    )
}